// =============================================================================
// SimSense — Car Discovery Page (OT-235: Recent Cars from user telemetry)
// =============================================================================

import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../state/useStore";

interface CarSummary {
  name: string;
  sessionCount: number;
  lastDriven: string;
  bestLapTime: number | null;
}

export function Cars() {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { sessions, fetchSessions } = useStore();

  useEffect(() => {
    document.title = "Cars | SimSense";
    fetchSessions().finally(() => setLoading(false));
  }, [fetchSessions]);

  // Build user's recent cars from their sessions
  const recentCars = useMemo<CarSummary[]>(() => {
    const carMap = new Map<string, { count: number; lastDate: string; bestLap: number | null }>();
    for (const s of sessions) {
      if (!s.carName) continue;
      const existing = carMap.get(s.carName);
      if (!existing) {
        carMap.set(s.carName, { count: 1, lastDate: s.sessionDate, bestLap: s.bestLapTime ?? null });
      } else {
        existing.count++;
        if (s.sessionDate > existing.lastDate) existing.lastDate = s.sessionDate;
        if (s.bestLapTime && (!existing.bestLap || s.bestLapTime < existing.bestLap)) {
          existing.bestLap = s.bestLapTime;
        }
      }
    }
    return Array.from(carMap.entries())
      .map(([name, data]) => ({
        name,
        sessionCount: data.count,
        lastDriven: data.lastDate,
        bestLapTime: data.bestLap,
      }))
      .sort((a, b) => b.lastDriven.localeCompare(a.lastDriven));
  }, [sessions]);

  const filteredRecent = useMemo(() => {
    if (!search) return recentCars;
    return recentCars.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
  }, [recentCars, search]);

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading cars...</div>;

  return (
    <div>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h2 style={{ fontSize: "28px", marginBottom: "8px" }}>Cars</h2>
          <p style={{ color: "var(--text2)" }}>Your driven cars from uploaded telemetry.</p>
        </div>
        <div style={{ width: "300px" }}>
          <input
            type="text"
            placeholder="Search cars..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", padding: "10px 16px", background: "var(--card)", border: "1px solid var(--border)", color: "#fff", borderRadius: "8px" }}
          />
        </div>
      </div>

      {/* Your Cars */}
      {filteredRecent.length > 0 && (
        <div>
          <h3 style={{ fontSize: "14px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "16px", letterSpacing: "1px" }}>
            {search ? "Search Results" : "Your Cars"}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px" }}>
            {filteredRecent.map(car => (
              <Link
                key={car.name}
                to={`/leaderboard?car=${encodeURIComponent(car.name)}`}
                style={{
                  background: "var(--card)",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid var(--border)",
                  textDecoration: "none",
                  color: "inherit",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <h3 style={{ fontSize: "17px", marginBottom: "12px" }}>{car.name}</h3>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "var(--text2)" }}>
                  <span>{car.sessionCount} session{car.sessionCount !== 1 ? "s" : ""}</span>
                  <span>{formatRelativeDate(car.lastDriven)}</span>
                  {car.bestLapTime && car.bestLapTime > 0 && (
                    <span style={{ color: "var(--good)", fontVariantNumeric: "tabular-nums" }}>
                      {formatLapTime(car.bestLapTime)}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {filteredRecent.length === 0 && (
        <div style={{ padding: "80px", textAlign: "center", background: "var(--card)", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "32px", marginBottom: "16px" }}>🏎️</div>
          <div style={{ color: "var(--text2)" }}>
            {search ? `No cars found matching "${search}"` : "No cars yet. Upload a session to see your cars here."}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatLapTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3);
  return mins > 0 ? `${mins}:${secs.padStart(6, "0")}` : `${secs}s`;
}
